import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Search, LifeBuoy, Mail, MessageSquare, ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HELP_CATEGORIES, HELP_UPDATED } from "@/data/helpCenter";
import LocaleToggle from "@/components/LocaleToggle";
import { useLocale } from "@/hooks/useLocale";

const T = {
  eyebrow: { en: "Help Center", es: "Centro de ayuda" },
  h1: { en: "How can we help?", es: "¿Cómo podemos ayudar?" },
  intro: {
    en: (
      <>Answers to the questions we hear most about accounts, ideas, grants, partners, and privacy. Can't find it here? Email </>
    ),
    es: (
      <>Respuestas a las preguntas más comunes sobre cuentas, ideas, subvenciones, aliados y privacidad. ¿No lo encuentra? Escriba a </>
    ),
  },
  introEnd: { en: <>.</>, es: <>.</> },
  searchPh: {
    en: "Search the FAQ (e.g. grants, MOU, 2FA)",
    es: "Buscar en la ayuda (p. ej. subvenciones, MOU, 2FA)",
  },
  searchAria: { en: "Search the help center", es: "Buscar en el centro de ayuda" },
  updated: { en: "Last updated", es: "Última actualización" },
  navAria: { en: "Help categories", es: "Categorías de ayuda" },
  noResults: { en: "No answers match", es: "No hay respuestas para" },
  noResultsHint: {
    en: <>Try a shorter phrase, or email </>,
    es: <>Pruebe una frase más corta o escriba a </>,
  },
  noResultsTail: { en: <> and we'll help directly.</>, es: <> y le ayudaremos directamente.</> },
  emailUs: { en: "Email us", es: "Escríbanos" },
  emailBody: {
    en: "For account, billing, partner, and general questions. We aim to reply within 2 business days.",
    es: "Para preguntas de cuenta, pagos, aliados y generales. Nuestra meta es responder en 2 días hábiles.",
  },
  slaLink: { en: "See response-time commitments →", es: "Vea los tiempos de respuesta →" },
  related: { en: "Related resources", es: "Recursos relacionados" },
};

const Help = () => {
  const [query, setQuery] = useState("");
  const { locale } = useLocale();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HELP_CATEGORIES;
    return HELP_CATEGORIES.map((c) => ({
      ...c,
      faqs: c.faqs.filter(
        (f) =>
          f.q[locale].toLowerCase().includes(q) ||
          f.a[locale].toLowerCase().includes(q),
      ),
    })).filter((c) => c.faqs.length > 0);
  }, [query, locale]);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: HELP_CATEGORIES.flatMap((c) =>
      c.faqs.map((f) => ({
        "@type": "Question",
        name: f.q.en,
        acceptedAnswer: { "@type": "Answer", text: f.a.en },
      })),
    ),
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Help Center — The Emerging Market</title>
        <meta
          name="description"
          content="Answers about accounts, ideas, grants, partners, billing, and privacy on emrkt.co. Search the FAQ or contact us at info@emrkt.co."
        />
        <link rel="canonical" href="https://emrkt.co/help" />
        <link rel="alternate" hrefLang="en" href="https://emrkt.co/help" />
        <link rel="alternate" hrefLang="es" href="https://emrkt.co/help" />
        <link rel="alternate" hrefLang="x-default" href="https://emrkt.co/help" />
        <meta property="og:title" content="Help Center — The Emerging Market" />
        <meta
          property="og:description"
          content="Search the FAQ, or reach us directly. Public help center for emrkt.co."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://emrkt.co/help" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <SiteHeader />

      <main className="container mx-auto px-6 py-16 max-w-5xl">
        <header className="mb-10">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary font-semibold">
              <LifeBuoy className="h-4 w-4" />
              <span>{T.eyebrow[locale]}</span>
            </div>
            <LocaleToggle />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            {T.h1[locale]}
          </h1>
          <p className="text-muted-foreground text-lg max-w-3xl">
            {T.intro[locale]}
            <a
              href="mailto:info@emrkt.co"
              className="text-primary underline"
            >
              info@emrkt.co
            </a>
            {T.introEnd[locale]}
          </p>

          <div className="relative mt-8 max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={T.searchPh[locale]}
              aria-label={T.searchAria[locale]}
              className="pl-9 h-11"
            />
          </div>

          <p className="text-xs text-muted-foreground mt-6">
            {T.updated[locale]} {HELP_UPDATED}
          </p>
        </header>

        {!query && (
          <nav
            aria-label={T.navAria[locale]}
            className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-12"
          >
            {HELP_CATEGORIES.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                className="rounded-lg border border-border bg-card p-4 hover:border-primary/50 transition-colors"
              >
                <div className="font-display font-semibold text-foreground text-sm">
                  {c.title[locale]}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {c.blurb[locale]}
                </div>
              </a>
            ))}
          </nav>
        )}

        <div className="space-y-12">
          {filtered.length === 0 && (
            <div className="rounded-lg border border-border bg-card p-8 text-center">
              <p className="text-foreground font-semibold mb-1">
                {T.noResults[locale]} "{query}".
              </p>
              <p className="text-sm text-muted-foreground">
                {T.noResultsHint[locale]}
                <a href="mailto:info@emrkt.co" className="text-primary underline">
                  info@emrkt.co
                </a>
                {T.noResultsTail[locale]}
              </p>
            </div>
          )}

          {filtered.map((c) => (
            <section key={c.id} id={c.id} className="scroll-mt-24">
              <div className="mb-4">
                <h2 className="font-display text-2xl font-bold">{c.title[locale]}</h2>
                <p className="text-sm text-muted-foreground mt-1">{c.blurb[locale]}</p>
              </div>
              <Accordion type="multiple" className="border border-border rounded-lg bg-card divide-y divide-border">
                {c.faqs.map((f, i) => (
                  <AccordionItem
                    key={i}
                    value={`${c.id}-${i}`}
                    className="border-0 px-4"
                  >
                    <AccordionTrigger className="text-left font-medium hover:no-underline">
                      {f.q[locale]}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      {f.a[locale]}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          ))}
        </div>

        <section className="mt-16 grid md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-border bg-card p-6">
            <Mail className="h-5 w-5 text-primary mb-3" />
            <h3 className="font-display font-semibold mb-1">{T.emailUs[locale]}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {T.emailBody[locale]}
            </p>
            <a
              href="mailto:info@emrkt.co"
              className="text-sm text-primary underline inline-flex items-center gap-1"
            >
              info@emrkt.co <ArrowRight className="h-3 w-3" />
            </a>
            <Link
              to="/contact"
              className="block mt-2 text-xs text-muted-foreground hover:text-primary"
            >
              {T.slaLink[locale]}
            </Link>
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <MessageSquare className="h-5 w-5 text-primary mb-3" />
            <h3 className="font-display font-semibold mb-1">{T.related[locale]}</h3>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li>
                <Link to="/partners/handbook" className="hover:text-primary">Partner &amp; Advocate Handbook</Link>
              </li>
              <li>
                <Link to="/pricing" className="hover:text-primary">Pricing &amp; plans</Link>
              </li>
              <li>
                <Link to="/status" className="hover:text-primary">System status</Link>
              </li>
              <li>
                <Link to="/methodology" className="hover:text-primary">Data methodology</Link>
              </li>
              <li>
                <Link to="/trust" className="hover:text-primary">Trust center</Link>
              </li>
            </ul>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Help;